import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'http://localhost:8000';

const EXCLUSION_REASONS = {
  not_tracked: 'Nicht vollständig getrackt',
  cheat_day: 'Cheat Day',
  sick: 'Krank',
  estimated: 'Geschätzt',
  other: 'Sonstiges'
};

function DailyOverview() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: `${new Date().getFullYear()}-01-01`, end: '' });
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '2000' });
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);

      const response = await axios.get(`${API_URL}/api/metrics/?${params.toString()}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const toggleExclusion = async (dateStr, currentlyExcluded, reason = 'other') => {
    try {
      const params = new URLSearchParams({
        target_date: dateStr,
        exclude: (!currentlyExcluded).toString()
      });
      if (!currentlyExcluded) {
        params.append('reason', reason);
      }
      await axios.post(`${API_URL}/api/data/exclude-day?${params.toString()}`);
      fetchData();
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  const handleEditNotes = (row) => {
    setEditingNotes(row.date);
    setNotesValue(row.notes || '');
  };

  const handleSaveNotes = async (dateStr) => {
    try {
      await axios.put(`${API_URL}/api/metrics/${dateStr}`, { notes: notesValue });
      setEditingNotes(null);
      fetchData();
    } catch (err) {
      alert('Fehler beim Speichern: ' + err.message);
    }
  };

  const handleCancelNotes = () => {
    setEditingNotes(null);
    setNotesValue('');
  };

  // Filter data
  const filteredData = data.filter(row => {
    if (filter === 'all') return true;
    if (filter === 'low_calories') return row.calories_consumed && row.calories_consumed < 1000;
    if (filter === 'high_calories') return row.calories_consumed && row.calories_consumed > 3000;
    if (filter === 'missing_nutrition') return !row.calories_consumed;
    if (filter === 'missing_activity') return !row.calories_burned;
    if (filter === 'excluded') return row.exclude_from_trends;
    if (filter === 'high_deficit') return row.calorie_deficit && row.calorie_deficit > 1500;
    return true;
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (valA === null || valA === undefined) valA = sortDir === 'asc' ? Infinity : -Infinity;
    if (valB === null || valB === undefined) valB = sortDir === 'asc' ? Infinity : -Infinity;

    if (sortField === 'date') {
      valA = new Date(valA);
      valB = new Date(valB);
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate stats
  const withCalIn = filteredData.filter(d => d.calories_consumed != null);
  const withCalOut = filteredData.filter(d => d.calories_burned != null);
  const withDeficit = filteredData.filter(d => d.calorie_deficit != null);
  const stats = {
    total: filteredData.length,
    avgCalIn: withCalIn.length ? Math.round(withCalIn.reduce((s, d) => s + d.calories_consumed, 0) / withCalIn.length) : 0,
    avgCalOut: withCalOut.length ? Math.round(withCalOut.reduce((s, d) => s + d.calories_burned, 0) / withCalOut.length) : 0,
    avgDeficit: withDeficit.length ? Math.round(withDeficit.reduce((s, d) => s + d.calorie_deficit, 0) / withDeficit.length) : 0
  };

  const SortHeader = ({ field, label }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortField === field && (sortDir === 'asc' ? '▲' : '▼')}
    </th>
  );

  if (loading) return <div className="loading"><div className="spinner"></div>Lade Daten...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="daily-overview">
      <h2>Tagesübersicht</h2>

      {/* Filters & Stats */}
      <div className="controls">
        <div className="filters">
          <label>
            Filter:
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">Alle Tage ({data.length})</option>
              <option value="low_calories">Niedrige Kalorien &lt;1000 ({data.filter(d => d.calories_consumed && d.calories_consumed < 1000).length})</option>
              <option value="high_calories">Hohe Kalorien &gt;3000 ({data.filter(d => d.calories_consumed && d.calories_consumed > 3000).length})</option>
              <option value="high_deficit">Hohes Defizit &gt;1500 ({data.filter(d => d.calorie_deficit && d.calorie_deficit > 1500).length})</option>
              <option value="missing_nutrition">Ohne Ernährung ({data.filter(d => !d.calories_consumed).length})</option>
              <option value="missing_activity">Ohne Aktivität ({data.filter(d => !d.calories_burned).length})</option>
              <option value="excluded">Ausgeschlossen ({data.filter(d => d.exclude_from_trends).length})</option>
            </select>
          </label>

          <label>
            Von:
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            />
          </label>

          <label>
            Bis:
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            />
          </label>

          <button onClick={() => setDateRange({ start: '', end: '' })} className="reset-btn">
            Reset
          </button>
        </div>

        <div className="stats-bar">
          <span><strong>{stats.total}</strong> Tage</span>
          <span>Ø Aufnahme: <strong>{stats.avgCalIn}</strong> kcal</span>
          <span>Ø Verbrauch: <strong>{stats.avgCalOut}</strong> kcal</span>
          <span>Ø Defizit: <strong>{stats.avgDeficit}</strong> kcal</span>
        </div>
      </div>

      {/* Data Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <SortHeader field="date" label="Datum" />
              <SortHeader field="calories_consumed" label="Kcal rein" />
              <SortHeader field="calories_burned" label="Kcal raus" />
              <SortHeader field="calorie_deficit" label="Defizit" />
              <SortHeader field="weight_kg" label="Gewicht" />
              <SortHeader field="protein_g" label="Protein" />
              <SortHeader field="carbs_g" label="Carbs" />
              <SortHeader field="fat_g" label="Fett" />
              <SortHeader field="steps" label="Schritte" />
              <th>Notizen</th>
              <th className="center-header">Status</th>
              <th className="center-header">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map(row => (
              <tr
                key={row.id}
                className={`
                  ${row.exclude_from_trends ? 'excluded' : ''}
                  ${row.calories_consumed && row.calories_consumed < 1000 ? 'warning-low' : ''}
                  ${row.calories_consumed && row.calories_consumed > 3000 ? 'warning-high' : ''}
                  ${row.calorie_deficit && row.calorie_deficit > 1500 ? 'warning-deficit' : ''}
                  ${row.is_interpolated ? 'interpolated' : ''}
                `}
              >
                <td className="date-cell">{row.date}</td>
                <td className={`cal-in ${!row.calories_consumed ? 'missing' : ''}`}>
                  {row.calories_consumed || '-'}
                </td>
                <td className={`cal-out ${!row.calories_burned ? 'missing' : ''}`}>
                  {row.calories_burned || '-'}
                </td>
                <td className={`deficit ${row.calorie_deficit > 0 ? 'positive' : row.calorie_deficit < 0 ? 'negative' : ''}`}>
                  {row.calorie_deficit || '-'}
                </td>
                <td className="weight">{row.weight_kg ? Number(row.weight_kg).toFixed(1) : '-'}</td>
                <td>{row.protein_g ? Math.round(row.protein_g) : '-'}</td>
                <td>{row.carbs_g ? Math.round(row.carbs_g) : '-'}</td>
                <td>{row.fat_g ? Math.round(row.fat_g) : '-'}</td>
                <td>{row.steps || '-'}</td>

                <td className="notes-cell">
                  {editingNotes === row.date ? (
                    <div className="notes-edit">
                      <input
                        type="text"
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveNotes(row.date);
                          if (e.key === 'Escape') handleCancelNotes();
                        }}
                        autoFocus
                      />
                      <button onClick={() => handleSaveNotes(row.date)} className="save-btn">✓</button>
                      <button onClick={handleCancelNotes} className="cancel-btn">✕</button>
                    </div>
                  ) : (
                    <span className="notes-text" onClick={() => handleEditNotes(row)} title="Klicken zum Bearbeiten">
                      {row.notes || <span className="notes-placeholder">+</span>}
                    </span>
                  )}
                </td>

                <td className="status-cell">
                  {row.exclude_from_trends && <span className="badge excluded" title={EXCLUSION_REASONS[row.exclusion_reason] || 'Ausgeschlossen'}>X</span>}
                  {row.is_interpolated && <span className="badge interpolated" title="Interpoliert">~</span>}
                  {row.is_provisional && <span className="badge provisional" title="Provisorisch">?</span>}
                  {row.outlier_verified && <span className="badge verified" title="Verifiziert">✓</span>}
                </td>

                <td className="actions-cell">
                  {row.exclude_from_trends ? (
                    <button onClick={() => toggleExclusion(row.date, true)} className="include-btn" title="Wieder einschließen">✓</button>
                  ) : (
                    <select
                      onChange={(e) => { if (e.target.value) toggleExclusion(row.date, false, e.target.value); e.target.value = ''; }}
                      defaultValue=""
                      className="exclude-select"
                      title="Ausschließen"
                    >
                      <option value="" disabled>X</option>
                      {Object.entries(EXCLUSION_REASONS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .daily-overview {
          padding: var(--space-4, 1rem);
          max-width: 1400px;
          margin: 0 auto;
        }

        h2 {
          margin-bottom: var(--space-4, 1rem);
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .controls {
          background: var(--card-bg, white);
          padding: var(--space-5, 1.25rem);
          border-radius: var(--radius-lg, 12px);
          margin-bottom: var(--space-4, 1rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .filters {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-4, 1rem);
          align-items: center;
          margin-bottom: var(--space-3, 0.75rem);
        }

        .filters label {
          display: flex;
          align-items: center;
          gap: var(--space-2, 0.5rem);
          font-weight: 500;
          color: var(--text-secondary, #6b7280);
          font-size: 0.9rem;
        }

        .filters select, .filters input {
          padding: var(--space-2, 0.5rem) var(--space-3, 0.75rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          background-color: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .filters select:focus, .filters input:focus {
          border-color: var(--primary-500, #3b82f6);
          box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
          outline: none;
        }

        .reset-btn {
          padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
          background: var(--text-secondary, #6b7280);
          color: white;
          border: none;
          border-radius: var(--radius-md, 8px);
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .reset-btn:hover {
          background: var(--text-primary, #1f2937);
        }

        .stats-bar {
          display: flex;
          gap: var(--space-6, 1.5rem);
          font-size: 0.9rem;
          color: var(--text-secondary, #6b7280);
          flex-wrap: wrap;
        }

        .stats-bar strong {
          color: var(--text-primary, #1f2937);
          font-weight: 700;
        }

        .table-container {
          overflow-x: auto;
          background: var(--card-bg, white);
          border-radius: var(--radius-lg, 12px);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }

        th, td {
          padding: var(--space-3, 0.75rem) var(--space-3, 0.75rem);
          text-align: right;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          white-space: nowrap;
        }

        th {
          background: var(--bg-color, #f9fafb);
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-muted, #9ca3af);
          position: sticky;
          top: 0;
        }

        th:hover {
          background: var(--border-color, #e5e7eb);
        }

        th.center-header {
          text-align: center;
        }

        tbody tr {
          transition: background-color 0.15s;
        }

        tbody tr:hover {
          background-color: var(--primary-50, #eff6ff);
        }

        .date-cell {
          text-align: left;
          font-weight: 600;
          color: var(--text-primary, #1f2937);
        }

        .missing {
          color: var(--text-muted, #9ca3af);
        }

        .deficit.positive {
          color: var(--success-600, #16a34a);
          font-weight: 600;
        }

        .deficit.negative {
          color: var(--error-500, #ef4444);
          font-weight: 600;
        }

        tr.warning-low {
          background: var(--warning-50, #fffbeb);
        }

        tr.warning-low:hover {
          background: var(--warning-100, #fef3c7);
        }

        tr.warning-high {
          background: var(--error-50, #fef2f2);
        }

        tr.warning-high:hover {
          background: var(--error-100, #fee2e2);
        }

        tr.warning-deficit {
          background: var(--success-50, #f0fdf4);
        }

        tr.warning-deficit:hover {
          background: var(--success-100, #dcfce7);
        }

        tr.excluded {
          background: var(--bg-color, #f9fafb);
          opacity: 0.6;
        }

        tr.interpolated {
          font-style: italic;
        }

        .notes-cell {
          text-align: left;
          min-width: 120px;
          max-width: 160px;
          width: 160px;
          white-space: nowrap;
          overflow: hidden;
        }

        .notes-text {
          cursor: pointer;
          color: var(--text-secondary, #6b7280);
          font-size: 0.8rem;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: inline-block;
          vertical-align: bottom;
        }

        .notes-text:hover {
          color: var(--primary-500, #3b82f6);
        }

        .notes-placeholder {
          color: var(--text-muted, #9ca3af);
          font-size: 1rem;
          line-height: 1;
        }

        .notes-edit {
          display: flex;
          align-items: center;
          gap: 3px;
          white-space: nowrap;
        }

        .notes-edit input {
          width: 80px;
          padding: 3px 6px;
          border: 2px solid var(--primary-500, #3b82f6);
          border-radius: var(--radius-sm, 6px);
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
          font-size: 0.8rem;
        }

        .notes-edit input:focus {
          outline: none;
          box-shadow: 0 0 0 2px var(--primary-100, #dbeafe);
        }

        .status-cell {
          text-align: center;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          font-size: 0.7rem;
          font-weight: bold;
          margin: 0 2px;
          transition: transform 0.2s;
        }

        .badge:hover {
          transform: scale(1.1);
        }

        .badge.excluded {
          background: var(--error-500, #ef4444);
          color: white;
        }

        .badge.interpolated {
          background: var(--warning-500, #f59e0b);
          color: white;
        }

        .badge.provisional {
          background: var(--text-secondary, #6b7280);
          color: white;
        }

        .badge.verified {
          background: var(--success-500, #22c55e);
          color: white;
        }

        .actions-cell {
          text-align: center;
          min-width: 60px;
          width: 60px;
          white-space: nowrap;
        }

        .actions-cell button {
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          margin: 0 2px;
          border: none;
          border-radius: var(--radius-sm, 6px);
          cursor: pointer;
          font-size: 0.85rem;
          vertical-align: middle;
          transition: all 0.15s;
        }

        .save-btn {
          background: var(--success-500, #22c55e);
          color: white;
          padding: 4px 8px;
          font-size: 0.8rem;
          min-width: 28px;
        }

        .save-btn:hover {
          background: var(--success-600, #16a34a);
        }

        .cancel-btn {
          background: var(--text-secondary, #6b7280);
          color: white;
          padding: 4px 8px;
          font-size: 0.8rem;
          min-width: 28px;
        }

        .cancel-btn:hover {
          background: var(--text-primary, #1f2937);
        }

        .include-btn {
          background: var(--success-500, #22c55e);
          color: white;
        }

        .include-btn:hover {
          background: var(--success-600, #16a34a);
        }

        .exclude-select {
          min-width: 28px;
          height: 26px;
          padding: 0 var(--space-1, 0.25rem) !important;
          background: var(--warning-500, #f59e0b);
          border: 1px solid var(--warning-600, #d97706);
          border-radius: var(--radius-sm, 6px);
          cursor: pointer;
          font-size: 0.75rem;
          color: white;
          font-weight: 600;
        }

        .exclude-select:hover {
          background: var(--warning-600, #d97706);
        }

        .loading, .error {
          padding: var(--space-8, 2rem);
          text-align: center;
        }

        .error {
          color: var(--error-500, #ef4444);
        }

        @media (max-width: 768px) {
          .filters {
            flex-direction: column;
            align-items: flex-start;
          }

          .stats-bar {
            flex-direction: column;
            gap: var(--space-2, 0.5rem);
          }

          table {
            font-size: 0.8rem;
          }

          th, td {
            padding: var(--space-2, 0.5rem);
          }

          .actions-cell button {
            min-height: 44px;
            min-width: 44px;
          }

          .exclude-select {
            min-height: 44px;
          }
        }
      `}</style>
    </div>
  );
}

export default DailyOverview;
